import fetcher_v2 from "@/api/fetcher_v2";
import { Article, RecentEvent } from "@/types/Dashboard";

import { URLS } from "../urls";
import { SearchLight } from "@/types/Search";
import { getOngoingSearches } from "./searches";
import { formatDateForApi } from "@/utils";
import { sub } from "date-fns";
import { generatePath } from "react-router-dom";
import { METRIC } from "@/constants";

export const getDashboardData = () => {
  return fetcher_v2<{
    recent_events: RecentEvent[];
    articles: {
      checklist_articles: Article[];
      help_articles: Article[];
      tips_articles: Article[];
    };
  }>(URLS.DASHOARD);
};

export const getReadDashboardArticles = () => {
  return fetcher_v2<{ read_dashboard_articles: number[] }>(
    URLS.READ_DASHBOARD_ARTICLES
  );
};

export const setReadDashboardArticle = (articleId: number) => {
  return fetcher_v2<string[]>(URLS.READ_DASHBOARD_ARTICLES, {
    body: { article_id: articleId },
    method: "PUT",
  });
};

export type MentionsOverTimeDay = { [searchId: string]: number } & {
  date: string;
};

const getGraphData = (
  searchesWithTopTotalNumMentionsInsight: Array<
    SearchLight & {
      totalNumMentionsInsight: {
        results: {
          labels: string[];
          dataset: Array<{
            data: number[];
          }>;
        };
      };
    }
  >
) => {
  const labels =
    searchesWithTopTotalNumMentionsInsight[0]?.totalNumMentionsInsight.results
      ?.labels ?? [];

  const totalMentionsOverTime = labels.map((label) => {
    const dateItem: MentionsOverTimeDay = {
      date: label,
    } as MentionsOverTimeDay;

    searchesWithTopTotalNumMentionsInsight.forEach((search) => {
      dateItem[search.uuid] =
        search.totalNumMentionsInsight.results.dataset[0]?.data[
          labels.indexOf(label)
        ];
    });

    return dateItem;
  });

  return totalMentionsOverTime;
};

export type SearchWithTotalNumMentionsInsight = SearchLight & {
  totalNumMentionsInsight: { results: { dataset: { data: number[] }[] } };
};

export const sortSearchesByTotalNumMentions = (
  a: SearchWithTotalNumMentionsInsight,
  b: SearchWithTotalNumMentionsInsight
) => {
  return (
    (b.totalNumMentionsInsight.results.dataset[0]?.data.reduce(
      (acc, curr) => acc + curr,
      0
    ) ?? 0) -
    (a.totalNumMentionsInsight.results.dataset[0]?.data.reduce(
      (acc, curr) => acc + curr,
      0
    ) ?? 0)
  );
};

const getSearchTotalNumMentionsInsight = async (uuid: Uuid) => {
  return fetcher_v2<{
    results: {
      labels: string[];
      dataset: Array<{
        data: number[];
      }>;
    };
  }>(
    generatePath(URLS.INSIGHTS_ANALYTICS_WIDGETS, {
      search_id: uuid,
      metric: METRIC.INSIGHTS_NUM_MENTIONS_OVER_TIME,
    }),
    {
      body: {
        start_date: formatDateForApi(sub(new Date(), { months: 3 })),
        end_date: formatDateForApi(new Date()),
      },
    }
  );
};

export const getNewDashboardData = async (maxSearchCount: number) => {
  const searches = await getOngoingSearches();

  if (!searches.ok) return null;

  const searchesWithTopTotalNumMentionsInsight = await Promise.all(
    searches.data.map(async (search) => {
      const totalNumMentionsInsight = await getSearchTotalNumMentionsInsight(
        search.uuid
      );

      return {
        ...search,
        totalNumMentionsInsight: totalNumMentionsInsight.ok
          ? totalNumMentionsInsight.data
          : { results: { labels: [], dataset: [] } },
      };
    })
  );

  const totalMentionsOverTime = getGraphData(
    searchesWithTopTotalNumMentionsInsight
  );

  const sortedSearchesWithTopTotalNumMentionsInsight =
    searchesWithTopTotalNumMentionsInsight.toSorted(
      sortSearchesByTotalNumMentions
    );

  const initialSearchIds = sortedSearchesWithTopTotalNumMentionsInsight
    .slice(0, maxSearchCount)
    .map((search) => search.uuid);

  return {
    searches: sortedSearchesWithTopTotalNumMentionsInsight,
    totalMentionsOverTime,
    initialSearchIds,
  };
};
